/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W3 — ENERGY WHEEL (Rotary Heat-Exchanger) rated-output
 * sheet (§33.3).
 *
 * React port of `ewheel.rated.outputs.tmpl` (1,218 lines) + the parts of
 * `energy_wheel.selector.js` that drive it (`render_outputs` :2784,
 * `sortRotaryHtexTable` :1594, `activateOutputLabel` :1611,
 * `isSummerOrWinterTabActive` :3046, `compareSizes` :3480).
 *
 * The React port had been rendering the GENERIC 6-column <RatedOutputsTable>
 * from `ratingKit/`. Legacy renders a THREE-TAB output sheet in the col-xs-1 /
 * col-xs-11 split that every other selector uses:
 *
 *   Output | Heat Exchanger  -> `#<prefix>Properties_output` — the candidate
 *          |                    model list (sticky header + `#er-wheel-models-list`)
 *          | Performance     -> `#<prefix>Performance_output` — winter/summer
 *          |                    sub-tabs over the full psychrometric sheet
 *          | Notes           -> `#<prefix>Notes-output`
 *
 * -- LEGACY DOM VERBATIM ----------------------------------------------------
 * The rating stylesheet keys off these exact class lists, `col-xs-*` widths and
 * `data-name` attributes (`.required_sizes .sizes[data-index]`,
 * `.output-part .summer-data`, `.sort-icon.selected.rotated`, `.output-height`,
 * `.er-wheel-tab-content` ...). Nothing here is "tidied up": the duplicated
 * wrappers, the empty `<span class="percent"></span>` on OACF, the winter
 * Latent row's copy-pasted `data-name="ladb"` (tmpl:968 / :977 — a legacy bug
 * that dev still ships) and the summer/winter EATR class mismatch (tmpl:605 has
 * no `output-without-unit`, tmpl:1053 does) are all reproduced as-is.
 *
 * -- FORMATTING -------------------------------------------------------------
 * Legacy `numberFormatting()` only formats spans carrying `data-formatter_type`
 * (`integer` -> thousands separators, no decimals; `decimal` -> separators +
 * 2 dp). Everything else — model, airstream name, wheel_size, rotor_depth,
 * fin_pattern, Frost Control — prints RAW, exactly as the API serialised it.
 * Same rule as GasHeaterRatedOutputs.tsx; `get_one` / `get_one_decimal` mirror
 * handlebars/helpers.js:150-157.
 *
 * PRESENTATIONAL ONLY: no queries, no writes, no bus traffic. Every value is a
 * prop the caller (EnergyWheelSelector) already holds.
 */

import { useState } from 'react';
import { RatedOn } from './ratingKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';

// ---------------------------------------------------------------------------
// value helpers (see the FORMATTING note above)
// ---------------------------------------------------------------------------

/** data-formatter_type="decimal" — thousands separators + 2 dp. */
const dec = (v: unknown): string => (v == null || v === '' ? '' : formatDecimalDisplay(v));
/** data-formatter_type="integer" — thousands separators, no decimals. */
const int = (v: unknown): string => (v == null || v === '' ? '' : formatIntegerDisplay(v));
/** no data-formatter_type — handlebars prints the value as-is (null -> ''). */
const raw = (v: unknown): string => (v == null ? '' : String(v));
/** `get_one` (helpers.js:150-152) — first truthy non-object arg, else ''. */
const one = (...vals: unknown[]): unknown => vals.find((v) => v && typeof v !== 'object') ?? '';
/** `get_one_decimal` (helpers.js:154-157) — Number(get_one).toFixed(2), NO
 *  thousands separator, falsy (including 0) -> ''. */
const getOneDecimal = (...vals: unknown[]): string => {
  const res = Number(one(...vals));
  return res ? res.toFixed(2) : '';
};
/** handlebars `{{#if}}` truthiness — undefined/null/''/false/0/[] are empty.
 *  NB the STRING "0.00" is truthy, which is why DRF decimal zeroes still show. */
const hbTruthy = (v: unknown): boolean =>
  !(v == null || v === '' || v === false || v === 0 || (Array.isArray(v) && !v.length));
/** numeric coercion for the sort comparator (legacy `a[key] - b[key]`). */
const num = (v: unknown): number => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

/** the `.warning` colour compareSizes paints on an oversized required dimension
 *  (mirrors CoilSelector's AW_WARNING, common.css `.required_sizes .warning`). */
const AW_WARNING: React.CSSProperties = { color: '#dc362e' };

// ---------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------

export type EnergyWheelSeason = 'summer' | 'winter';
export type EnergyWheelOutputTab = 'properties' | 'performance' | 'notes';

/** A rated candidate row (`rated_erwheels[i]`) — every field is optional and
 *  read through the index signature, exactly as handlebars did. */
export type EnergyWheelRatedRow = Record<string, unknown>;

/** An air-stream option row (`airStreamCollection.toJSON()`). */
export interface EnergyWheelAirStream extends Record<string, unknown> {
  id?: number | string | null;
  name?: string | null;
}

/** A tunnel/section entry (`self.tunnel_indexes`, energy_wheel.selector.js:489-501).
 *  `index` is the tunnel's display index NAME, not an array position. */
export interface EnergyWheelTunnel extends Record<string, unknown> {
  index?: string | number | null;
  aw_width?: number | string | null;
  aw_height?: number | string | null;
  id?: number | string | null;
}

/** `splitedNotes` / `splitedRatingNotes` are key->value maps rendered as
 *  "key : value" (tmpl:1154 / :1183); a plain string[] is accepted too because
 *  the React selector keeps its warnings as a flat list. */
export type EnergyWheelNoteBag = Record<string, unknown> | string[] | null | undefined;

/** A manufacturer note row (`self.notes.toJSON()`). */
export interface EnergyWheelNote {
  note?: unknown;
  component_mfg_id?: unknown;
}

export interface EnergyWheelRatedOutputsProps {
  // ---- the candidate list -------------------------------------------------
  /** `rated_erwheels` — the rated candidate rows, in the caller's order. */
  rows: EnergyWheelRatedRow[];
  /** index INTO `rows` of the picked candidate (`rated_erwheel_index`). */
  ratedIndex: number;
  /** row click (`changeErWheel`, js:3066-3073) — receives the SOURCE index. */
  onSelectRow?: (index: number) => void;
  /** `rated_erwheel` / `rated_model`. Defaults to `rows[ratedIndex]`, falling
   *  back to `state` when nothing is rated (js:2883 `current_model.toJSON()`). */
  ratedModel?: EnergyWheelRatedRow | null;

  // ---- lookups ------------------------------------------------------------
  /** the selector's `s` state object — supplies `*_air_stream_*_id`,
   *  `wheel_size_id` (the sorting gate) and the rated-model fallbacks. */
  state: Record<string, unknown>;
  /** `airStreamCollection.toJSON()` — resolves the air-stream ids to names. */
  airStreams?: EnergyWheelAirStream[];
  /** `self.tunnel_indexes` — the per-tunnel REQUIRED AIRWAY index labels and the
   *  available W x H the required sizes are compared against (`compareSizes`).
   *  In EnergyWheelSelector this is `ewSections`. */
  tunnels?: EnergyWheelTunnel[];
  /** `fin_pattern_name` — the label of the selected fin-pattern option, the
   *  `get_one` fallback for a row with no `fin_pattern` of its own (tmpl:141). */
  finPatternName?: string | null;
  /** `mfg_id`. Drives `showLeavingWb` (js:2977 — 52/60/233/237) and filters the
   *  manufacturer notes (js:2924-2926). */
  mfgId?: number | string | null;

  // ---- notes / messages ---------------------------------------------------
  /** render_outputs' `error` argument — collapses the sheet to the Notes tab. */
  errors?: string[];
  /** `splitedNotes` — the Warning block (tmpl:1142-1159). */
  warnings?: EnergyWheelNoteBag;
  /** `splitedRatingNotes` — the "Notes :" block (tmpl:1174-1189). */
  ratingNotes?: EnergyWheelNoteBag;
  /** the manufacturer notes collection — "Mfg's Notes :" (tmpl:1190-1203). */
  notes?: Array<EnergyWheelNote | string>;
  /** `voltageMismatch` (js:2932-2949) — RHE motor voltage != AHU main voltage. */
  voltageMismatch?: { message: string } | null;

  // ---- state flags --------------------------------------------------------
  /** `rated_datetime`. The rated row's own `rated_datetime` wins (tmpl:1217). */
  ratedDatetime?: string | null;
  /** `prefix` — '' in header mode, 'ahu-' on the board (js:2796). */
  idPrefix?: string;
  /** `notFound` (js:2882) — the API came back with no usable wheel. */
  notFound?: boolean;
  /** `failed` — the API call itself errored (tmpl:20-24 takes precedence). */
  failed?: boolean;
  /** `ok` — the API success flag. false greys the two model tabs
   *  (`non-clickable`, tmpl:4 / :7) and warns the Notes anchor (tmpl:11). */
  ok?: boolean;

  // ---- initial tab --------------------------------------------------------
  /** `self.activeOutputTabId` — the pane id (`<prefix>Performance_output`) that
   *  was selected before the re-render, restored verbatim (js:3005-3011). */
  activeOutputTabId?: string | null;
  /** the commented-out rule at js:2192-2199 — "input tab follows": when the
   *  INPUT nav sits on Performance the output opens on Performance, otherwise on
   *  Heat Exchanger. Pass `false` for the Heat Exchanger default; omit it and
   *  the LIVE code path (js:3012-3014, always Performance) applies. */
  inputTabIsPerformance?: boolean;
  /** notified when the user switches output tab, so the caller can keep its own
   *  `activeOutputTabId` (legacy stores it in `changeErWheel`). */
  onOutputTabChange?: (paneId: string) => void;
}

// ---------------------------------------------------------------------------
// small DOM helpers (each mirrors one repeated legacy block)
// ---------------------------------------------------------------------------

/** One value cell of the LEAVING SUPPLY / LEAVING EXHAUST pair (tmpl:232-254):
 *  `<div class="col-xs-3 [col-xs-offset-3] output-data"><div class="row">...`. */
const Cell = ({ offset, children }: { offset?: boolean; children?: React.ReactNode }) => (
  <div className={`col-xs-3${offset ? ' col-xs-offset-3' : ''} output-data`}>
    <div className="row">{children}</div>
  </div>
);

/** The standard left-column row: `col-xs-3` label + `col-xs-9` input-part
 *  holding the supply/exhaust cells (tmpl:226-256). */
const PairRow = ({ label, children }: { label: React.ReactNode; children?: React.ReactNode }) => (
  <div className="row">
    <div className="col-xs-3 label-part">{label}</div>
    <div className="col-xs-9 input-part">
      <div className="row">{children}</div>
    </div>
  </div>
);

/** Face Velocity / Air PD nest differently: the label sits inside a bare
 *  `<div class="">` and the two value cells are plain `col-xs-3`s inside ONE
 *  `<div class=" output-data">` (tmpl:409-437 / :438-472). The leading space in
 *  `class=" output-data"` is legacy's own — kept so the class list matches. */
const NestedPairRow = ({
  label,
  sa,
  ea,
}: {
  label: React.ReactNode;
  sa: React.ReactNode;
  ea: React.ReactNode;
}) => (
  <div className="row">
    <div className="col-xs-3 label-part">
      <div className="">{label}</div>
    </div>
    <div className="col-xs-9 input-part">
      <div className="row">
        <div className=" output-data">
          <div className="col-xs-3">
            <div className="row">{sa}</div>
          </div>
          <div className="col-xs-3 col-xs-offset-3">
            <div className="row">{ea}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** A right-hand column row (tmpl:475-505): `col-xs-5` label / `col-xs-7` value,
 *  the value area holding one or two `col-xs-6` cells. `rightRow` picks between
 *  the `right-row` label wrapper (capacities, Condensate, Frost Control) and the
 *  bare `<div class="">` one (OACF, EATR). */
const RightRow = ({
  label,
  rightRow = true,
  labelDataName,
  frostLayout,
  primary,
  secondary,
}: {
  label: React.ReactNode;
  rightRow?: boolean;
  labelDataName?: string;
  /** the Frost Control row moves `output-data` onto the row itself and drops the
   *  inner wrapper (tmpl:1095-1104). */
  frostLayout?: boolean;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
}) => (
  <div className="row">
    <div className="col-xs-5 label-part">
      <div className={rightRow ? 'right-row' : ''} data-name={labelDataName}>
        {label}
      </div>
    </div>
    <div className="col-xs-7 input-part">
      {frostLayout ? (
        <div className="row output-data">
          <div className="col-xs-6">
            <div className="row">{primary}</div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className=" output-data">
            <div className="col-xs-6">
              <div className="row">{primary}</div>
            </div>
            {secondary != null && (
              <div className="col-xs-6">
                <div className="row">{secondary}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
);

/** A `.sort-icon-wrapper` header cell (tmpl:52-59 / :78-85 / :86-93). */
const SortHeader = ({
  label,
  sortKey,
  hasSorting,
  sortBy,
  sortOrder,
  extraClass = '',
  onSort,
}: {
  label: string;
  sortKey: string;
  hasSorting: boolean;
  sortBy: string | null;
  sortOrder: 'asc' | 'desc';
  extraClass?: string;
  onSort: (key: string) => void;
}) => (
  <div
    className={`output-without-unit${hasSorting ? ' sort-icon-wrapper' : ''}${extraClass}`}
    data-name={sortKey}
    data-sort={sortKey}
    onClick={hasSorting ? () => onSort(sortKey) : undefined}
  >
    {label}
    {hasSorting && (
      <span
        className={`sort-icon${sortBy === sortKey ? ' selected' : ''}${
          sortBy === sortKey && sortOrder === 'asc' ? ' rotated' : ''
        }`}
        data-sort={sortKey}
      >
        <i className="icon-arrow-down-1" data-sort={sortKey} />
      </span>
    )}
  </div>
);

/** A Notes-pane group: `col-xs-2` title + `col-xs-10` body (tmpl:1128-1139). */
const NotesGroup = ({
  title,
  titleClass,
  items,
  itemClass,
  extraClass,
}: {
  title: string;
  titleClass: string;
  items: string[];
  itemClass: string;
  extraClass?: string;
}) =>
  items.length ? (
    <div className={`col-xs-12${extraClass ? ` ${extraClass}` : ''}`}>
      <div className="row">
        <div className="col-xs-2">
          <p className={titleClass}>{title}</p>
        </div>
        <div className="col-xs-10">
          {items.map((t, i) => (
            <p className={itemClass} key={i}>
              {t}
            </p>
          ))}
        </div>
      </div>
    </div>
  ) : null;

/** `splitedNotes` / `splitedRatingNotes` -> the rendered lines. A key->value map
 *  becomes "key : value" and drops falsy values (`{{#if @this}}`, tmpl:1153);
 *  a flat string[] is passed through. */
const noteLines = (bag: EnergyWheelNoteBag): string[] => {
  if (!bag) return [];
  if (Array.isArray(bag)) return bag.filter(Boolean).map((v) => String(v));
  return Object.entries(bag)
    .filter(([, v]) => hbTruthy(v))
    .map(([k, v]) => `${k} : ${String(v)}`);
};

// ---------------------------------------------------------------------------
// the output nav (tmpl:1-14)
// ---------------------------------------------------------------------------

const OUTPUT_TABS: ReadonlyArray<{
  tab: EnergyWheelOutputTab;
  label: string;
  /** the legacy pane id sans prefix (also the `data-tab` value). */
  id: string;
  /** the legacy <li> class sans prefix. */
  nav: string;
}> = [
  { tab: 'properties', label: 'Heat Exchanger', id: 'Properties_output', nav: 'Properties-output' },
  { tab: 'performance', label: 'Performance', id: 'Performance_output', nav: 'Performance-output' },
  { tab: 'notes', label: 'Notes', id: 'Notes-output', nav: 'Notes-output' },
];

// ---------------------------------------------------------------------------
// component
// ---------------------------------------------------------------------------

export const EnergyWheelRatedOutputs = ({
  rows,
  ratedIndex,
  onSelectRow,
  ratedModel,
  state,
  airStreams = [],
  tunnels = [],
  finPatternName,
  mfgId,
  errors,
  warnings,
  ratingNotes,
  notes,
  voltageMismatch,
  ratedDatetime,
  idPrefix = 'ahu-',
  notFound,
  failed,
  ok = true,
  activeOutputTabId,
  inputTabIsPerformance,
  onOutputTabChange,
}: EnergyWheelRatedOutputsProps) => {
  const errs = (errors ?? []).filter(Boolean);

  // ---- initial output tab (js:3004-3015) --------------------------------
  // an error pins the sheet to Notes; otherwise the caller's remembered pane id
  // wins; otherwise the live default is Performance (the commented-out
  // "input tab follows" variant at js:2192-2199 is reachable by passing
  // inputTabIsPerformance={false}).
  const [outputTab, setOutputTab] = useState<EnergyWheelOutputTab>(
    errs.length
      ? 'notes'
      : (OUTPUT_TABS.find((t) => `${idPrefix}${t.id}` === activeOutputTabId)?.tab ??
          (inputTabIsPerformance === false ? 'properties' : 'performance'))
  );

  // ---- summer / winter sub-tab (js:3046-3063) ---------------------------
  // render_outputs forces WINTER when there is no summer airflow (js:2987-3003);
  // otherwise legacy lands on summer. The choice also drives the `hide` class on
  // the `.summer-data` / `.winter-data` FV spans in the Heat Exchanger pane —
  // isSummerOrWinterTabActive toggles those across the whole `.output-part`.
  const [season, setSeason] = useState<EnergyWheelSeason>(
    hbTruthy(state.oa_airflow_actual_summer) ? 'summer' : 'winter'
  );

  // ---- sorting (sortRotaryHtexTable, js:1594-1609) ----------------------
  // `has_sorting` (js:2975): more than one candidate AND no explicitly chosen
  // wheel size. No column is pre-selected; each click toggles asc/desc.
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const hasSorting = rows.length > 1 && !hbTruthy(state.wheel_size_id);

  // ---- the rated model the Performance / Notes panes read ---------------
  // js:2883 — the picked row when it carries data, else the saved model.
  const picked = ratedModel ?? rows[ratedIndex];
  const r: EnergyWheelRatedRow =
    picked && Object.keys(picked).length ? picked : (state as EnergyWheelRatedRow);

  // ---- the candidate list, in display order -----------------------------
  // `er_wheels = self.rated_erwheels || [rated_model]` (js:2884): with nothing
  // rated the sheet still lists the saved model as a single row, patched from
  // state where the API fields are absent (js:2913-2920).
  const sourceRows: EnergyWheelRatedRow[] = rows.length
    ? rows
    : [
        {
          ...r,
          wheel_size: r.wheel_size ?? state.wheel_size_id,
          rotor_depth: r.rotor_depth ?? state.rotor_depth,
          fin_pattern: r.fin_pattern ?? state.fin_pattern ?? 'Standard',
        },
      ];

  // display order = caller order -> sort (when a column is active) -> the
  // optimal (smallest wheel_size) row hoisted to the front, which js:2888-2912
  // does on EVERY render, i.e. after the sort too.
  let display = sourceRows.map((row, srcIndex) => ({ row, srcIndex }));
  if (sortBy) {
    const key = sortBy;
    const dir = sortOrder === 'asc' ? 1 : -1;
    display = [...display].sort((a, b) => dir * (num(a.row[key]) - num(b.row[key])));
  }
  // the optimal model: smallest wheel_size (unparseable sizes count as Infinity).
  let optimalSrc = -1;
  let optimalSize = Infinity;
  sourceRows.forEach((row, i) => {
    const parsed = parseFloat(String(row.wheel_size));
    const v = isNaN(parsed) ? Infinity : parsed;
    if (v < optimalSize) {
      optimalSize = v;
      optimalSrc = i;
    }
  });
  if (optimalSrc >= 0 && optimalSize !== Infinity) {
    const at = display.findIndex((d) => d.srcIndex === optimalSrc);
    if (at > 0) display.unshift(...display.splice(at, 1));
  } else {
    optimalSrc = -1;
  }

  const onSort = (key: string) => {
    const nextOrder: 'asc' | 'desc' =
      key === sortBy ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortBy(key);
    setSortOrder(nextOrder);
    // js:1605-1607 — sorting re-selects the first row of the NEW order.
    if (onSelectRow && sourceRows.length) {
      const dir = nextOrder === 'asc' ? 1 : -1;
      const head = sourceRows
        .map((row, srcIndex) => ({ row, srcIndex }))
        .sort((a, b) => dir * (num(a.row[key]) - num(b.row[key])))[0];
      if (head) onSelectRow(head.srcIndex);
    }
  };

  const selectTab = (tab: EnergyWheelOutputTab) => {
    setOutputTab(tab);
    const meta = OUTPUT_TABS.find((t) => t.tab === tab);
    if (meta && onOutputTabChange) onOutputTabChange(`${idPrefix}${meta.id}`);
  };

  // ---- derived flags ----------------------------------------------------
  // js:2977 — Heatex(52) / Innergytech(60) / AI-Rotor(233) / Annexair(237) are
  // the only manufacturers that return leaving WB temperatures.
  const showLeavingWb = [52, 60, 233, 237].includes(Number(mfgId));
  const warnLines = noteLines(warnings);
  const ratingNoteLines = noteLines(ratingNotes);
  // js:2924-2926 — only this manufacturer's notes.
  const mfgNotes: string[] = (notes ?? [])
    .map((n): EnergyWheelNote => (typeof n === 'string' ? { note: n } : n))
    .filter((n) =>
      mfgId == null || n.component_mfg_id == null ? true : n.component_mfg_id == mfgId
    )
    .map((n) => raw(n.note))
    .filter(Boolean);
  // tmpl:11 — the Notes anchor turns into a warning when there is anything to
  // warn about.
  const notesWarn = warnLines.length > 0 || !!voltageMismatch || ok === false;
  // tmpl:29 `{{#ifCompare ok 'or' rated_erwheel}}` — the two model panes exist
  // when the call succeeded OR a model is already rated.
  const hasModelPanes = ok || hbTruthy(r.model);
  // tmpl:1217 — the rated row's own timestamp beats the selector-level one.
  const ratedOn = (r.rated_datetime as string | null | undefined) ?? ratedDatetime ?? null;

  const paneClass = (tab: EnergyWheelOutputTab, extra = '') =>
    `tab-pane fade scrollable-content col-xs-12 float-left output-height${extra}${
      outputTab === tab ? ' in active' : ''
    }`;

  /** Resolve an air-stream id to its name. Legacy loops the collection inside an
   *  `{{#ifCompare}}`, so an unmatched id renders NO CELL AT ALL (tmpl:197-209). */
  const airStreamName = (key: string): string | null => {
    const id = state[key];
    if (id == null || id === '') return null;
    const hit = airStreams.find((a) => a.id == id);
    return hit ? raw(hit.name) : null;
  };

  // -------------------------------------------------------------------------
  // the winter / summer performance sheet (tmpl:173-616 summer, :617-1108 winter)
  // -------------------------------------------------------------------------
  const seasonPane = (sea: EnergyWheelSeason) => {
    const f = (base: string) => `${base}_${sea}`;
    const oaName = airStreamName(`oa_air_stream_${sea}_id`);
    const raName = airStreamName(`ra_air_stream_${sea}_id`);
    return (
      <div
        id={sea === 'summer' ? 'summer-o' : 'winter-o'}
        className={`${sea} tab-pane fade${season === sea ? ' in active' : ''}`}
      >
        {/* ---- left column (tmpl:174-473) ---- */}
        <div className="col-xs-12 col-md-12 col-lg-7" style={{ paddingRight: 45 }}>
          {/* column headings (tmpl:175-190) */}
          <div className="row">
            <div className="col-xs-offset-3 col-xs-9 input-part">
              <div className="row subtitles">
                <Cell>
                  <span className="output-without-unit">LEAVING SUPPLY AIR</span>
                </Cell>
                <Cell offset>
                  <span className="output-without-unit">LEAVING EXHAUST AIR</span>
                </Cell>
              </div>
            </div>
          </div>

          {/* Airstream (tmpl:191-225) — raw names, no formatter. */}
          <PairRow label="Airstream">
            {oaName != null && (
              <Cell>
                <span
                  className="number_output output-without-unit"
                  data-name={`oa_air_stream_${sea}_id`}
                >
                  {oaName}
                </span>
              </Cell>
            )}
            {raName != null && (
              <Cell offset>
                <span
                  className="number_output output-without-unit"
                  data-name={`ra_air_stream_${sea}_id`}
                >
                  {raName}
                </span>
              </Cell>
            )}
          </PairRow>

          {/* Airflow (tmpl:226-256) */}
          <PairRow label="Airflow">
            <Cell>
              <span className="percent">CFM</span>
              <span
                className="number_output"
                data-formatter_type="integer"
                data-name={f('sa_airflow_actual')}
              >
                {int(r[f('sa_airflow_actual')])}
              </span>
            </Cell>
            <Cell offset>
              <span className="percent">CFM</span>
              <span
                className="number_output"
                data-formatter_type="integer"
                data-name={f('ea_airflow_actual')}
              >
                {int(r[f('ea_airflow_actual')])}
              </span>
            </Cell>
          </PairRow>

          {/* DB Temperature (tmpl:257-287) */}
          <PairRow label="DB Temperature">
            <Cell>
              <span className="percent temp">F</span>
              <span
                className="number_output"
                data-formatter_type="decimal"
                data-name={f('sa_db_temp')}
              >
                {dec(r[f('sa_db_temp')])}
              </span>
            </Cell>
            <Cell offset>
              <span className="percent temp">F</span>
              <span
                className="number_output"
                data-formatter_type="decimal"
                data-name={f('ea_db_temp')}
              >
                {dec(r[f('ea_db_temp')])}
              </span>
            </Cell>
          </PairRow>

          {/* WB Temperature — `showLeavingWb` manufacturers only (tmpl:288-320) */}
          {showLeavingWb && (
            <PairRow label="WB Temperature">
              <Cell>
                <span className="percent temp">F</span>
                <span
                  className="number_output"
                  data-formatter_type="decimal"
                  data-name={f('sa_wb_temp')}
                >
                  {dec(r[f('sa_wb_temp')])}
                </span>
              </Cell>
              <Cell offset>
                <span className="percent temp">F</span>
                <span
                  className="number_output"
                  data-formatter_type="decimal"
                  data-name={f('ea_wb_temp')}
                >
                  {dec(r[f('ea_wb_temp')])}
                </span>
              </Cell>
            </PairRow>
          )}

          {/* Humidity (tmpl:321-377) — FOUR `col-xs-3` cells and NO offsets: the
              HR/RH pair for supply, then the HR/RH pair for exhaust. The `%` unit
              only prints when the RH value itself is truthy (tmpl:340 / :364). */}
          <PairRow label="Humidity">
            <Cell>
              <span className="percent">gr/lb</span>
              <span className="number_output" data-formatter_type="decimal" data-name={f('sa_hr')}>
                {dec(r[f('sa_hr')])}
              </span>
            </Cell>
            <Cell>
              {hbTruthy(r[f('sa_rh')]) && <span className="percent unit-between">%</span>}
              <span className="number_output" data-formatter_type="decimal" data-name={f('sa_rh')}>
                {dec(r[f('sa_rh')])}
              </span>
            </Cell>
            <Cell>
              <span className="percent">gr/lb</span>
              <span className="number_output" data-formatter_type="decimal" data-name={f('ea_hr')}>
                {dec(r[f('ea_hr')])}
              </span>
            </Cell>
            <Cell>
              {hbTruthy(r[f('ea_rh')]) && <span className="percent unit-between">%</span>}
              <span className="number_output" data-formatter_type="decimal" data-name={f('ea_rh')}>
                {dec(r[f('ea_rh')])}
              </span>
            </Cell>
          </PairRow>

          {/* Enthalpy (tmpl:378-408) — the `long-input` value class. */}
          <PairRow label="Enthalpy">
            <Cell>
              <span className="percent">btu/lb</span>
              <span
                className="long-input number_output"
                data-formatter_type="decimal"
                data-name={f('sa_w')}
              >
                {dec(r[f('sa_w')])}
              </span>
            </Cell>
            <Cell offset>
              <span className="percent">btu/lb</span>
              <span
                className="long-input number_output"
                data-formatter_type="decimal"
                data-name={f('ea_w')}
              >
                {dec(r[f('ea_w')])}
              </span>
            </Cell>
          </PairRow>

          {/* Face Velocity (tmpl:409-437 / :854-885) */}
          <NestedPairRow
            label="Face Velocity"
            sa={
              <>
                <span className="percent">fpm</span>
                <span
                  data-formatter_type="integer"
                  className="number_output"
                  data-name={f('sa_face_velocity')}
                >
                  {int(r[f('sa_face_velocity')])}
                </span>
              </>
            }
            ea={
              <>
                <span className="percent"> fpm </span>
                <span
                  className="number_output"
                  data-formatter_type="integer"
                  data-name={f('ea_face_velocity')}
                >
                  {int(r[f('ea_face_velocity')])}
                </span>
              </>
            }
          />

          {/* Air PD (tmpl:438-472 / :886-920) */}
          <NestedPairRow
            label="Air PD"
            sa={
              <>
                <span className="percent"> in WC </span>
                <span
                  className="number_output"
                  data-formatter_type="decimal"
                  data-name={f('sa_apd')}
                >
                  {dec(r[f('sa_apd')])}
                </span>
              </>
            }
            ea={
              <>
                <span className="percent"> in WC </span>
                <span
                  className="number_output"
                  data-formatter_type="decimal"
                  data-name={f('ea_apd')}
                >
                  {dec(r[f('ea_apd')])}
                </span>
              </>
            }
          />
        </div>

        {/* ---- right column (tmpl:474-615 summer, :922-1107 winter) ---- */}
        <div className="col-xs-12 col-md-12 col-lg-5 top-margin">
          {/* Sensible (tmpl:475-505 / :923-953) — the label really is wrapped in
              a <span> on this row alone. Efficiency prints only when truthy. */}
          <RightRow
            label={<span>Sensible</span>}
            primary={
              <>
                <span className="percent">btu/hr</span>
                <span
                  className=" number_output"
                  data-formatter_type="integer"
                  data-name={f('sensible_capacity')}
                >
                  {int(r[f('sensible_capacity')])}
                </span>
              </>
            }
            secondary={
              hbTruthy(r[f('sensible_efficiency')]) ? (
                <>
                  <span className="percent">%</span>
                  <span
                    className=" number_output"
                    data-formatter_type="decimal"
                    data-name={f('sensible_efficiency')}
                  >
                    {dec(r[f('sensible_efficiency')])}
                  </span>
                </>
              ) : undefined
            }
          />

          {/* Latent (tmpl:506-536 / :954-984). The WINTER spans carry
              `data-name="ladb"` on BOTH capacity and efficiency — a copy-paste
              from the coil sheet that dev still ships, reproduced verbatim so the
              DOM matches. Summer uses the correct names. */}
          <RightRow
            label="Latent"
            primary={
              <>
                <span className="percent">btu/hr</span>
                <span
                  className=" number_output"
                  data-formatter_type="integer"
                  data-name={sea === 'winter' ? 'ladb' : 'latent_capacity_summer'}
                >
                  {int(r[f('latent_capacity')])}
                </span>
              </>
            }
            secondary={
              hbTruthy(r[f('latent_efficiency')]) ? (
                <>
                  <span className="percent">%</span>
                  <span
                    className=" number_output"
                    data-formatter_type="decimal"
                    data-name={sea === 'winter' ? 'ladb' : 'latent_efficiency_summer'}
                  >
                    {dec(r[f('latent_efficiency')])}
                  </span>
                </>
              ) : undefined
            }
          />

          {/* Total (tmpl:537-567 / :985-1015) */}
          <RightRow
            label="Total"
            primary={
              <>
                <span className="percent">btu/hr</span>
                <span
                  className=" number_output"
                  data-formatter_type="integer"
                  data-name={f('total_capacity')}
                >
                  {int(r[f('total_capacity')])}
                </span>
              </>
            }
            secondary={
              hbTruthy(r[f('total_efficiency')]) ? (
                <>
                  <span className="percent">%</span>
                  <span
                    className=" number_output"
                    data-formatter_type="decimal"
                    data-name={f('total_efficiency')}
                  >
                    {dec(r[f('total_efficiency')])}
                  </span>
                </>
              ) : undefined
            }
          />

          {/* OACF (tmpl:568-591 / :1016-1039) — the empty `<span class="percent">`
              is legacy's own spacer, not an oversight. */}
          <RightRow
            label="OACF"
            rightRow={false}
            primary={
              <>
                <span className="percent" />
                <span
                  className="number_output output-without-unit"
                  data-formatter_type="decimal"
                  data-name={f('oacf')}
                >
                  {dec(r[f('oacf')])}
                </span>
              </>
            }
          />

          {/* EATR (tmpl:592-614 / :1040-1063). The two seasons disagree on the
              value class — summer is `number_output`, winter adds
              `output-without-unit`. Ported as written. */}
          <RightRow
            label="EATR"
            rightRow={false}
            primary={
              <>
                <span className="percent">%</span>
                <span
                  className={
                    sea === 'winter' ? 'number_output output-without-unit' : 'number_output'
                  }
                  data-formatter_type="decimal"
                  data-name={f('eatr')}
                >
                  {dec(r[f('eatr')])}
                </span>
              </>
            }
          />

          {/* Condensate + Frost Control are WINTER ONLY (tmpl:1064-1106). */}
          {sea === 'winter' && (
            <>
              <RightRow
                label="Condensate"
                primary={
                  <>
                    <span className="percent">gpm</span>
                    <span
                      className="medium-input number_output"
                      data-formatter_type="decimal"
                      data-name="condensate_winter"
                    >
                      {dec(r.condensate_winter)}
                    </span>
                  </>
                }
              />
              {/* tmpl:1088-1106 — `output-data` sits on the ROW here, the label
                  div carries the data-name, and the value is the raw literal
                  "Yes " / "No " (trailing space included). */}
              <RightRow
                label="Frost Control"
                labelDataName="frost_control_winter"
                frostLayout
                primary={
                  <span className="output-without-unit" data-name="frost_control_winter">
                    {hbTruthy(r.frost_control_winter) ? 'Yes ' : 'No '}
                  </span>
                }
              />
            </>
          )}
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // render
  // -------------------------------------------------------------------------
  return (
    <>
      {/* ===== output tab nav (tmpl:1-14) ===== */}
      <div className="col-xs-1 nav nav-tabs">
        <ul className="ouptut_tubs coil-raiting">
          <li className="cursor-default">Output</li>
          {OUTPUT_TABS.map(({ tab, label, id, nav }) => {
            // tmpl:4 / :7 — the two model tabs pick up `non-clickable` when the
            // API said no, and the Heat Exchanger <li> alone carries
            // `output_label_heatex`.
            const isNotes = tab === 'notes';
            const cls = [
              `${idPrefix}${nav}`,
              'output_label',
              tab === 'properties' ? 'output_label_heatex' : '',
              !isNotes && ok === false ? 'non-clickable' : '',
              outputTab === tab ? 'selected' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <li key={tab} data-tab={`${idPrefix}${id}`} className={cls}>
                {/* bootstrap's tab plugin preventDefaults the click; the href is
                    kept because an <a> without one is not keyboard-focusable. */}
                <a
                  className={isNotes && notesWarn ? 'warning-notes' : undefined}
                  data-toggle="tab"
                  href={`#${idPrefix}${id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isNotes && ok === false) return;
                    selectTab(tab);
                  }}
                >
                  {label}
                </a>
              </li>
            );
          })}
        </ul>
      </div>

      {/* ===== the sheet (tmpl:15-1214) ===== */}
      <div
        className="col-xs-11 with-border tab-content er-wheel-tab-content"
        style={{ float: 'right' }}
      >
        <div className="with-bottom-distance row">
          <div className="col-xs-12 content-data">
            <div className="row full-height">
              {notFound ? (
                // tmpl:19-27 — a hard API failure outranks "nothing matched".
                failed ? (
                  <div className="failed error-message">
                    <div>API Error! </div>
                    <div>Contact your administrator.</div>
                  </div>
                ) : (
                  <div className="error-message">
                    {' '}
                    Unable to find an Energy Wheel that satisfies all inputted parameters.
                  </div>
                )
              ) : (
                <>
                  {hasModelPanes && (
                    <>
                      {/* ---- Heat Exchanger: the candidate list (tmpl:30-159) ---- */}
                      <div
                        className={paneClass('properties')}
                        id={`${idPrefix}Properties_output`}
                        {...{ name: `${idPrefix}Properties_output` }}
                      >
                        <div className="col-xs-12 col-md-12">
                          {/* sticky header (tmpl:35-98) */}
                          <div className="row sticky-row">
                            <div className="col-xs-4 label-part">MODEL</div>
                            <div className="col-xs-8 input-part">
                              <div className="row">
                                <div className="col-xs-5 output-data">
                                  <div className="row">
                                    <div className="required_sizes right" data-name="model">
                                      <span>REQUIRED AIRWAY WIDTH x HEIGHT (in)</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-xs-1 output-data">
                                  <div className="row">
                                    <SortHeader
                                      label="SIZE"
                                      sortKey="wheel_size"
                                      hasSorting={hasSorting}
                                      sortBy={sortBy}
                                      sortOrder={sortOrder}
                                      onSort={onSort}
                                    />
                                  </div>
                                </div>
                                <div className="col-xs-2 output-data">
                                  <div className="row">
                                    <div className="output-without-unit right" data-name="model">
                                      DEPTH (in)
                                    </div>
                                  </div>
                                </div>
                                <div className="col-xs-2 output-data">
                                  <div className="row">
                                    <div className="output-without-unit" data-name="model">
                                      FIN PATTERN
                                    </div>
                                  </div>
                                </div>
                                {/* the FV header exists TWICE — one winter, one
                                    summer — and the season toggle hides the other
                                    (js:3050-3062). tmpl:78 / :86 always emit
                                    `sort-icon-wrapper` on these two, unlike SIZE. */}
                                <div className="col-xs-2 output-data">
                                  <div className="row">
                                    <SortHeader
                                      label="FV (fpm)"
                                      sortKey="sa_face_velocity_winter"
                                      hasSorting={hasSorting}
                                      sortBy={sortBy}
                                      sortOrder={sortOrder}
                                      extraClass={` sort-icon-wrapper winter-data right${
                                        season === 'winter' ? '' : ' hide'
                                      }`}
                                      onSort={onSort}
                                    />
                                    <SortHeader
                                      label="FV (fpm)"
                                      sortKey="sa_face_velocity_summer"
                                      hasSorting={hasSorting}
                                      sortBy={sortBy}
                                      sortOrder={sortOrder}
                                      extraClass={` sort-icon-wrapper summer-data right${
                                        season === 'summer' ? '' : ' hide'
                                      }`}
                                      onSort={onSort}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* the candidate rows (tmpl:99-157) */}
                          <div id="er-wheel-models-list">
                            {display.map(({ row, srcIndex }, i) => {
                              // tmpl:111-119 — one `.sizes` span per tunnel; the
                              // width is a scalar on the row, the heights an array
                              // indexed alongside `tunnels`.
                              //
                              // ★ `required_aw_height` is only an ARRAY when the
                              // unit has a tunnel divider: get_required_aw_height
                              // (erwheel/utils.py:34-48) returns `[btm, top]` for
                              // 'horizontal', `[full, full]` for 'vertical' and the
                              // bare `full_height` NUMBER for an undivided unit —
                              // every 1 x 1, and the hybrids whose tunnels only run
                              // back to back along X. Legacy's `{{#each}}` iterated
                              // a number into nothing, so the whole REQUIRED AIRWAY
                              // cell — the width included, it is drawn inside this
                              // loop — came out blank there too. The undivided
                              // height applies to each tunnel the wheel sits in, so
                              // spread the scalar across them (one row when there
                              // are none, e.g. header mode).
                              const rawHeight = row.required_aw_height;
                              const heights: unknown[] = Array.isArray(rawHeight)
                                ? (rawHeight as unknown[])
                                : rawHeight == null || rawHeight === ''
                                  ? []
                                  : tunnels.length > 0
                                    ? tunnels.map(() => rawHeight)
                                    : [rawHeight];
                              const widthTxt = getOneDecimal(row.required_aw_width);
                              return (
                                <div
                                  key={srcIndex}
                                  data-id={i}
                                  className={`row coil-counts-numbers coil_counts_numbers er-wheel-numbers${
                                    srcIndex === optimalSrc ? ' optimal-model' : ''
                                  }${srcIndex === ratedIndex ? ' active' : ''}`}
                                  onClick={onSelectRow ? () => onSelectRow(srcIndex) : undefined}
                                >
                                  <div
                                    className="col-xs-4 label-part"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    {raw(row.model)}
                                  </div>
                                  <div className="col-xs-8 input-part">
                                    <div className="row">
                                      <div className="col-xs-5 output-data">
                                        <div className="row">
                                          <div className="" data-name="required_sizes">
                                            <div className="required_sizes right">
                                              {heights.map((h, hi) => {
                                                // compareSizes (js:3480-3491): a
                                                // required dimension larger than
                                                // the tunnel's available airway is
                                                // flagged `warning`.
                                                const t = tunnels[hi];
                                                const heightTxt = getOneDecimal(h);
                                                const wWarn =
                                                  t != null &&
                                                  parseFloat(widthTxt) > num(t.aw_width);
                                                const hWarn =
                                                  t != null &&
                                                  parseFloat(heightTxt) > num(t.aw_height);
                                                return (
                                                  <span className="sizes" data-index={hi} key={hi}>
                                                    <span className="tunel_index_name">
                                                      {raw(t == null ? '' : t.index)} :{' '}
                                                    </span>
                                                    <span
                                                      className={`sizes_aw_width${
                                                        wWarn ? ' warning' : ''
                                                      }`}
                                                      style={wWarn ? AW_WARNING : undefined}
                                                    >
                                                      {widthTxt}
                                                    </span>{' '}
                                                    x{' '}
                                                    <span
                                                      className={`sizes_aw_height${
                                                        hWarn ? ' warning' : ''
                                                      }`}
                                                      style={hWarn ? AW_WARNING : undefined}
                                                    >
                                                      {heightTxt}
                                                    </span>{' '}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-xs-1 output-data">
                                        <div className="row">
                                          <div className="output-without-unit" data-name="wheel_size">
                                            {raw(row.wheel_size)}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-xs-2 output-data">
                                        <div className="row">
                                          <div
                                            className="output-without-unit right"
                                            data-name="rotor_depth"
                                          >
                                            {raw(row.rotor_depth)}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-xs-2 output-data">
                                        <div className="row">
                                          <div className="output-without-unit" data-name="fin_pattern">
                                            {/* tmpl:141 `{{get_one fin_pattern ../fin_pattern_name}}` */}
                                            {raw(one(row.fin_pattern, finPatternName))}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-xs-2 output-data">
                                        <div className="row">
                                          <div
                                            className="output-without-unit right"
                                            data-name="sa_face_velocity"
                                          >
                                            <span
                                              className={`winter-data${
                                                season === 'winter' ? '' : ' hide'
                                              }`}
                                            >
                                              {raw(row.sa_face_velocity_winter)}
                                            </span>
                                            <span
                                              className={`summer-data${
                                                season === 'summer' ? '' : ' hide'
                                              }`}
                                            >
                                              {raw(row.sa_face_velocity_summer)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* ---- Performance (tmpl:160-1109) ---- */}
                      <div
                        className={paneClass('performance', ' Performance')}
                        id={`${idPrefix}Performance_output`}
                        {...{ name: `${idPrefix}Performance_output` }}
                      >
                        {/* model name + season sub-tabs (tmpl:164-172) */}
                        <div className="col-xs-12 pt-10 mb-25">
                          <div className="row">
                            <div className="rated_erwheel_model">{raw(r.model)}</div>
                            <ul className="col-xs-12 text-right summer-winter-tabing">
                              <li className={`winter-tab${season === 'winter' ? ' active' : ''}`}>
                                <a
                                  data-toggle="tab"
                                  data-tab="winter"
                                  href="#winter-o"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setSeason('winter');
                                  }}
                                >
                                  winter
                                </a>
                              </li>
                              <li className={`summer-tab${season === 'summer' ? ' active' : ''}`}>
                                <a
                                  data-toggle="tab"
                                  data-tab="summer"
                                  href="#summer-o"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setSeason('summer');
                                  }}
                                >
                                  summer
                                </a>
                              </li>
                            </ul>
                          </div>
                        </div>
                        {/* legacy emits summer first, then winter (tmpl:173 / :617). */}
                        {seasonPane('summer')}
                        {seasonPane('winter')}
                      </div>
                    </>
                  )}

                  {/* ---- Notes (tmpl:1112-1209) — present even when the model
                       panes are suppressed. ---- */}
                  <div
                    className={paneClass('notes')}
                    id={`${idPrefix}Notes-output`}
                    {...{ name: `${idPrefix}Notes-output` }}
                  >
                    <div className="col-xs-12 col-md-12 full-height">
                      {/* tmpl:1117-1124 — legacy opens a `<div class="row">` inside
                          the `{{#if}}` and never closes it (the `{{/if}}` lands
                          after the inner div). React cannot emit unbalanced
                          markup, so the wrapper is closed here; this is the ONE
                          structural difference from the template. */}
                      {hbTruthy(r.model) && (
                        <div className="row">
                          <div className="col-xs-12 pt-10 mb-25 pos-absolute">
                            <div className="row">
                              <div className="rated_erwheel_model">{raw(r.model)}</div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="col-xs-12">
                        <div className="clearfix row nots_and_errors er-wheel">
                          {/* tmpl:1127-1140 — an error REPLACES everything below. */}
                          <NotesGroup
                            title="Error :"
                            titleClass="warning-notes"
                            items={errs}
                            itemClass="warning-msg"
                          />
                          {!errs.length && (
                            <>
                              {/* tmpl:1141-1173 — Warning: the voltage mismatch
                                  first, then the split rating warnings. With no
                                  split notes but a mismatch present, legacy still
                                  draws the block (the else branch). */}
                              <NotesGroup
                                title="Warning : "
                                titleClass="warning-notes"
                                items={[
                                  ...(voltageMismatch ? [voltageMismatch.message] : []),
                                  ...warnLines,
                                ]}
                                itemClass="warning-notes label"
                              />
                              {/* tmpl:1174-1189 */}
                              <NotesGroup
                                title="Notes :"
                                titleClass="label"
                                items={ratingNoteLines}
                                itemClass="model-notes label"
                                extraClass="rated-notes"
                              />
                              {/* tmpl:1190-1203 */}
                              <NotesGroup
                                title="Mfg's Notes :"
                                titleClass="label"
                                items={mfgNotes}
                                itemClass=" label"
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ===== "Rated on : <datetime>" (tmpl:1215-1217) ===== */}
      <div className="col-xs-11 col-xs-offset-1 ">
        <RatedOn datetime={ratedOn} />
      </div>
    </>
  );
};

export default EnergyWheelRatedOutputs;
